import { IMG, CONTACT } from "@/lib/site";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import { Newspaper, Radio, Tv, ExternalLink, Calendar, PlayCircle, Clock } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { useT } from "@/lib/i18n";

const LV_ARTICLE_URL = "https://www.lavanguardia.com/empresas-de-vanguardia/salud/20260204/11443164/marta-montardit-especialista-acompanamiento-oncologico-diagnostico-te-quita-autonomia-acompanamiento-te-la-devuelve.html";
const RAC1_ARTICLE_URL = "https://www.rac1.cat/salut/20260309/322276/marta-montardit-superar-cancer-es-formava-professora-ioga-ajuda-pacients-batalla-calgui-guanyar-cami-cal-aprendre-transitar-acompanyat-lv.html";

const Prensa = () => {
  const t = useT();
  useSeo({
    title: {
      es: "Prensa · La Vanguardia, RAC1, Primera Persona TV | Método TAO",
      ca: "Premsa · La Vanguardia, RAC1, Primera Persona TV | Mètode TAO",
    },
    description: {
      es: "Apariciones de Marta Montardit en La Vanguardia, RAC1 y Primera Persona TV hablando sobre acompañamiento oncológico, Método TAO y la importancia del enfoque cuerpo-mente.",
      ca: "Aparicions de la Marta Montardit a La Vanguardia, RAC1 i Primera Persona TV parlant sobre acompanyament oncològic, Mètode TAO i la importància de l'enfocament cos-ment.",
    },
    path: "/prensa",
  });

  const pullQuotes = [
    { quote: t("press.quote1"), theme: "sage" },
    { quote: t("press.quote2"), theme: "terracotta" },
    { quote: t("press.quote3"), theme: "olive" },
  ];

  // eslint-disable-next-line no-unused-vars
  const _contactRef = CONTACT;

  return (
  <div className="bg-ivory">
    {/* HERO */}
    <section className="pt-12 md:pt-20 pb-16 md:pb-24" data-testid="page-prensa">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-7">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">{t("press.eyebrow")}</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal leading-[0.98]">
            {t("press.title1")} <span className="italic text-terracotta">{t("press.title2")}</span>.
          </h1>
          <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
            {t("press.lead")}
          </p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="watermark-hide-ivory aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
            <img src={IMG.armsCrossed} alt="Marta Montardit" className="w-full h-full object-cover object-[center_30%] scale-105" fetchPriority="high" decoding="async" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* LA VANGUARDIA · Article */}
    <section className="py-20 md:py-28 bg-sand/30 grain" data-testid="press-lv">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <Reveal>
          <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-8">
            <span className="font-serif italic text-4xl md:text-5xl text-charcoal leading-none">La Vanguardia</span>
            <span className="h-6 w-px bg-charcoal/20 hidden sm:inline-block" />
            <span className="inline-flex items-center gap-2 text-charcoal/55 text-[11px] tracking-[0.3em] uppercase">
              <Calendar size={12} /> {t("press.lv.date")}
            </span>
            <span className="h-6 w-px bg-charcoal/20 hidden sm:inline-block" />
            <span className="inline-flex items-center gap-2 text-sage text-[11px] tracking-[0.3em] uppercase">
              <Newspaper size={12} /> {t("press.lv.category")}
            </span>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal delay={0.05} className="lg:col-span-7">
            <blockquote>
              <p className="font-serif text-3xl md:text-5xl text-charcoal leading-[1.08]">
                <span className="text-terracotta">"</span>
                {t("press.lv.quote")} <span className="italic">{t("press.lv.quoteItalic")}</span>.
                <span className="text-terracotta">"</span>
              </p>
              <footer className="mt-4 text-[11px] tracking-[0.3em] uppercase text-charcoal/50">
                {t("press.lv.author")}
              </footer>
            </blockquote>

            <p className="mt-10 text-[17px] md:text-[18px] leading-relaxed text-charcoal/80 font-light max-w-2xl">
              {t("press.lv.intro")}
            </p>

            <div className="mt-10 space-y-4 text-[15px] leading-relaxed text-charcoal/70 max-w-2xl">
              <p>{t("press.lv.p1")}</p>
              <p>
                {t("press.lv.p2")}{" "}
                <strong className="text-charcoal font-medium">Clínic</strong> o
                <strong className="text-charcoal font-medium"> Vall d'Hebron</strong>
                {t("press.lv.p2cont")}
              </p>
              <p>
                {t("press.lv.p3a")}{" "}
                <strong className="text-charcoal font-medium">Método TAO</strong>
                {t("press.lv.p3b")}
              </p>
            </div>

            <a
              href={LV_ARTICLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lv-article-link"
              className="mt-10 group inline-flex items-center gap-2 bg-charcoal text-ivory rounded-full px-7 py-3.5 text-sm font-medium hover:bg-terracotta transition-all"
            >
              {t("press.lv.btn")}
              <ExternalLink size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <a
              href={LV_ARTICLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lv-article-preview"
              className="block group relative"
            >
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl shadow-charcoal/15 border border-sand/70 transition-transform duration-500 group-hover:-translate-y-1">
                <img
                  src={IMG.lvArticleP1}
                  alt="Artículo completo de Marta Montardit en La Vanguardia"
                  className="w-full h-auto"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/5 transition-colors" />
              </div>
              <div className="absolute bottom-5 right-5 bg-ivory/95 backdrop-blur rounded-full px-4 py-2 text-[11px] tracking-[0.2em] uppercase text-charcoal flex items-center gap-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <ExternalLink size={12} /> {t("press.lv.openHover")}
              </div>
            </a>
            <p className="mt-4 text-[11px] tracking-[0.25em] uppercase text-charcoal/45 text-center">
              {t("press.lv.caption")}
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mt-20 md:mt-24">
          <div className="grid md:grid-cols-3 gap-6">
            {pullQuotes.map((q) => (
              <blockquote
                key={q.theme}
                data-testid={`lv-pullquote-${q.theme}`}
                className={`bg-ivory rounded-3xl p-8 border-l-4 ${
                  q.theme === "sage"
                    ? "border-sage"
                    : q.theme === "terracotta"
                    ? "border-terracotta"
                    : "border-olive"
                }`}
              >
                <p className="font-serif italic text-lg md:text-xl text-charcoal leading-snug">
                  "{q.quote}"
                </p>
                <footer className="mt-4 text-[10px] tracking-[0.3em] uppercase text-charcoal/45">— Marta Montardit</footer>
              </blockquote>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* RAC1 + Primera Persona (teasers) */}
    <section className="py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6">
        <Reveal>
          <article className="bg-charcoal text-ivory rounded-3xl p-10 md:p-12 h-full grain relative overflow-hidden flex flex-col" data-testid="press-rac1">
            <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full bg-terracotta/15 blur-3xl" />
            <div className="relative flex flex-col h-full">
              <div className="inline-flex flex-wrap items-center gap-3 text-[11px] tracking-[0.3em] uppercase mb-5">
                <span className="inline-flex items-center gap-2 text-terracotta">
                  <Radio size={14} /> {t("press.rac1.tag")}
                </span>
                <span className="text-ivory/30">·</span>
                <span className="inline-flex items-center gap-2 text-ivory/50">
                  <Calendar size={12} /> {t("press.rac1.date")}
                </span>
              </div>
              <h3 className="font-sans font-bold text-5xl md:text-6xl tracking-tight">RAC1</h3>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-ivory/90 leading-snug">
                {t("press.rac1.quote")}
              </p>
              <p className="mt-5 text-ivory/65 leading-relaxed text-[14px]">
                {t("press.rac1.body")}
              </p>
              <a
                href={RAC1_ARTICLE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rac1-article-link"
                className="mt-8 group inline-flex items-center gap-2 bg-terracotta text-charcoal rounded-full px-6 py-3 text-[13px] font-medium hover:bg-ivory transition-colors self-start"
              >
                {t("press.rac1.btn")}
                <ExternalLink size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
          </article>
        </Reveal>

        <Reveal delay={0.1}>
          <article className="bg-ivory border border-sand/70 rounded-3xl p-10 md:p-12 h-full relative overflow-hidden" data-testid="press-primera-persona">
            <div className="absolute -top-12 -left-8 w-40 h-40 rounded-full bg-sage/15 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-sage text-[11px] tracking-[0.3em] uppercase mb-5">
                <Tv size={14} /> {t("press.pp.tag")}
              </div>
              <h3 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1]">Primera<br />Persona</h3>
              <p className="mt-6 text-charcoal/70 leading-relaxed text-[15px]">
                {t("press.pp.body")}
              </p>

              <div className="mt-8 relative rounded-2xl overflow-hidden bg-charcoal aspect-video border border-sand/60" data-testid="primera-persona-video">
                <video
                  src="/press/primera-persona.mp4"
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label="Aparición de Marta Montardit en el programa Primera Persona"
                />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-charcoal/80 text-ivory rounded-full px-2.5 py-1 text-[9px] tracking-[0.22em] uppercase backdrop-blur">
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" /> {t("press.pp.muted")}
                </div>
              </div>

              <p className="mt-5 text-[11px] tracking-[0.3em] uppercase text-charcoal/45">
                {t("press.pp.caption")}
              </p>
            </div>
          </article>
        </Reveal>
      </div>
    </section>

    {/* FULL INTERVIEW · Primera Persona (entrevista íntegra) */}
    <section className="pb-24 md:pb-32" data-testid="press-full-interview">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="bg-charcoal text-ivory rounded-3xl overflow-hidden grain relative">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-terracotta/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-sage/15 blur-3xl pointer-events-none" />
            <div className="grid lg:grid-cols-12 gap-0 relative">
              <div className="lg:col-span-5 p-10 md:p-14 lg:p-16 flex flex-col justify-center">
                <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">
                  {t("press.pp.fullEyebrow")}
                </p>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                  {t("press.pp.fullTitle1")}{" "}
                  <span className="italic text-terracotta">{t("press.pp.fullTitle2")}</span>.
                </h2>
                <p className="mt-7 text-ivory/75 leading-relaxed text-[15px] md:text-[16px] font-light max-w-md">
                  {t("press.pp.fullLead")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[11px] tracking-[0.28em] uppercase text-ivory/55">
                  <span className="inline-flex items-center gap-2">
                    <Clock size={13} className="text-terracotta" /> {t("press.pp.fullDuration")}
                  </span>
                  <span className="text-ivory/25">·</span>
                  <span className="inline-flex items-center gap-2">
                    <Tv size={13} className="text-sage" /> {t("press.pp.fullChannel")}
                  </span>
                </div>
              </div>
              <div className="lg:col-span-7 lg:p-8 lg:pl-0">
                <div className="relative aspect-video bg-black lg:rounded-2xl overflow-hidden ring-1 ring-ivory/10" data-testid="full-interview-video-wrap">
                  <video
                    src="/press/entrevista-primera-persona.mp4"
                    poster="/press/entrevista-primera-persona-poster.jpg"
                    className="w-full h-full object-contain"
                    controls
                    preload="none"
                    playsInline
                    data-testid="full-interview-video"
                    aria-label={t("press.pp.fullPlay")}
                  />
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <SectionCTA />
  </div>
  );
};

export default Prensa;
